import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Container,
  Box,
  Alert,
  CircularProgress,
  Button,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Link,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  TextField,
  Autocomplete,
  Avatar,
  Tooltip,
  Skeleton
} from '@mui/material'
import {
  Dashboard as DashboardIcon,
  Refresh,
  OpenInNew,
  Assignment,
  Group,
  Warning,
  FilterList,
  Clear,
  ArrowUpward,
  ArrowDownward,
  Sort,
  Search,
  DarkMode,
  LightMode,
  Timeline,
  SearchOff,
  ClearAll,
  ViewList,
  ViewTimeline,
  Schedule,
  EventAvailable
} from '@mui/icons-material'
import { useQuery } from '@tanstack/react-query'
import { parseISO, isValid, isPast } from 'date-fns'
import { isOverdue, isPaused } from '../utils/dateUtils'
import { isInitiativeAtRisk } from '../utils/riskCalculations'

import config from '../config'
import { timelineService } from '../services/timelineService'
import { useJiraConfig, useJiraConfiguredQuery } from '../hooks/useJiraConfig'
import StatusFilter from '../components/StatusFilter'
import TeamFilter from '../components/TeamFilter'
import PMOAssigneeFilter from '../components/PMOAssigneeFilter'
import ThemeFilter from '../components/ThemeFilter'
import PMLeadFilter from '../components/PMLeadFilter'
import LabelsFilter from '../components/LabelsFilter'
import EndDateRangeFilter from '../components/EndDateRangeFilter'
import SavedViews from '../components/SavedViews'
import { useTeamEnrichedData } from '../hooks/useTeamEnrichedData'
import { useWaitForComprehensiveData } from '../hooks/useSchedulerStatus'
import ApiService from '../services/apiService'
import { useTheme } from '../App'
import UserProfile from '../components/auth/UserProfile'
import CollapsibleSection from '../components/CollapsibleSection'
import { processInitiatives, processTimelineData, ProcessedInitiative } from '../utils/dataProcessing'
import TimelineTable, { TimelineTableRef } from '../components/TimelineTable'
import TimelineDataToggle from '../components/TimelineDataToggle'
import StatusIndicators from '../components/StatusIndicators'
import { getStatusHexColor, filterActiveChildEpics } from '../utils/statusColors'
import Footer from '../components/Footer'
import StandardAppBar from '../components/StandardAppBar'
import { ProgressDisplay } from '../components/shared/ProgressDisplay'

// Sortable Header Component
interface SortableHeaderProps {
  label: string
  sortKey: string
  currentSort: { key: string | null; direction: 'asc' | 'desc' }
  onSort: (key: string) => void
  minWidth?: number
  align?: 'left' | 'center' | 'right'
}

const SortableHeader: React.FC<SortableHeaderProps> = ({
  label,
  sortKey,
  currentSort,
  onSort,
  minWidth = 120,
  align = 'left'
}) => {
  const getSortIcon = () => {
    if (currentSort.key !== sortKey) {
      return <Sort sx={{ fontSize: 16, opacity: 0.5 }} />
    }
    return currentSort.direction === 'asc'
      ? <ArrowUpward sx={{ fontSize: 16 }} />
      : <ArrowDownward sx={{ fontSize: 16 }} />
  }

  return (
    <TableCell sx={{ minWidth }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start',
          gap: 1,
          cursor: 'pointer',
          '&:hover': { backgroundColor: 'action.hover' },
          borderRadius: 1,
          p: 0.5
        }}
        onClick={() => onSort(sortKey)}
      >
        <Typography variant="subtitle2" fontWeight="bold" sx={{ whiteSpace: 'nowrap' }}>
          {label}
        </Typography>
        {getSortIcon()}
      </Box>
    </TableCell>
  )
}

function Dashboard() {
  const navigate = useNavigate()
  const { darkMode, toggleDarkMode } = useTheme()
  const [searchText, setSearchText] = useState('')
  const [sortConfig, setSortConfig] = useState<{
    key: string | null
    direction: 'asc' | 'desc'
  }>({ key: null, direction: 'asc' })


  // Status filter state
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([])

  const handleStatusChange = (statuses: string[]) => {
    setSelectedStatuses(statuses)
  }

  // Team filter state
  const [selectedTeams, setSelectedTeams] = useState<string[]>([])

  // PMO Assignee filter state
  const [selectedPMOAssignees, setSelectedPMOAssignees] = useState<string[]>([])

  // Theme filter state
  const [selectedThemes, setSelectedThemes] = useState<string[]>([])

  // PM Lead filter state
  const [selectedPMLeads, setSelectedPMLeads] = useState<string[]>([])

  // Labels filter state
  const [selectedLabels, setSelectedLabels] = useState<string[]>([])

  // End date range filter state
  const [endDateRangeStart, setEndDateRangeStart] = useState<string>('')
  const [endDateRangeEnd, setEndDateRangeEnd] = useState<string>('')



  // Missing data filter state
  const [showMissingDataOnly, setShowMissingDataOnly] = useState<boolean>(false)

  // Overdue Initiatives filter state
  const [showOverdueOnly, setShowOverdueOnly] = useState<boolean>(false)

  // Potential Risks filter state
  const [showPotentialRisksOnly, setShowPotentialRisksOnly] = useState<boolean>(false)

  // Timeline Health filter state
  const [showTimelineHealthOnly, setShowTimelineHealthOnly] = useState<boolean>(false)

  // Upcoming Deadlines filter state
  const [showUpcomingDeadlinesOnly, setShowUpcomingDeadlinesOnly] = useState<boolean>(false)

  // Timeline view state
  const [isTimelineView, setIsTimelineView] = useState<boolean>(false)

  // Ref for TimelineTable to enable programmatic scrolling
  const timelineTableRef = useRef<TimelineTableRef>(null)

  // Timeline data filter state
  const [showTimelineDataOnly, setShowTimelineDataOnly] = useState<boolean>(false)

  // Filter conflict detection state
  const [filterConflict, setFilterConflict] = useState<{
    hasConflict: boolean
    message: string
    suggestions: string[]
  } | null>(null)

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Ctrl/Cmd + K to focus global search
      if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
        event.preventDefault()
        const searchInput = document.querySelector('input[placeholder*="Search across all columns"]') as HTMLInputElement
        if (searchInput) {
          searchInput.focus()
        }
      }
      // Escape to clear all filters
      if (event.key === 'Escape') {
        setSearchText('')
        setSelectedStatuses([])
        setSelectedTeams([])
        setSelectedPMOAssignees([])
        setSelectedThemes([])
        setSelectedPMLeads([])
        setSelectedLabels([])
        setShowMissingDataOnly(false)
        setShowOverdueOnly(false)
        setShowPotentialRisksOnly(false)
        setShowTimelineHealthOnly(false)
        setShowUpcomingDeadlinesOnly(false)
        setSortConfig({ key: null, direction: 'asc' })
      }
      // Ctrl/Cmd + Shift + D to toggle dark mode
      if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key === 'D') {
        event.preventDefault()
        toggleDarkMode()
      }
      // Ctrl/Cmd + Shift + T to toggle timeline view
      if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key === 'T') {
        event.preventDefault()
        setIsTimelineView(!isTimelineView)
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])



  // Check Jira configuration status
  const { data: jiraConfig, isLoading: configLoading } = useJiraConfig();

  // Fetch initiatives data only when Jira is properly configured
  const {
    data: initiativesResponse,
    isLoading,
    isError,
    error,
    refetch
  } = useJiraConfiguredQuery(
    ['red-initiatives'],
    ApiService.getRedInitiatives,
    {
      refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
      retry: false, // Disable React Query retry since API service handles retries
      retryOnMount: false, // Don't retry on component mount
      refetchOnWindowFocus: false, // Don't refetch on window focus
      staleTime: 2 * 60 * 1000, // Consider data stale after 2 minutes
    }
  )

  // Get timeline data for initiatives
  const initiativeKeys = initiativesResponse?.data?.map((initiative: any) => initiative.key) || []
  const {
    data: timelineResponse,
    isLoading: timelineLoading,
    isError: timelineError
  } = useQuery({
    queryKey: ['timeline', initiativeKeys],
    queryFn: () => timelineService.getInitiativesTimeline(initiativeKeys),
    enabled: initiativeKeys.length > 0, // Only fetch when we have initiative keys
    refetchInterval: 10 * 60 * 1000, // Refetch every 10 minutes (less frequent than main data)
  })

  // Fetch team-enriched data when teams are selected
  // Use comprehensive filtering for the first selected team to get accurate results
  const selectedTeamForEnrichment = selectedTeams.length > 0 ? selectedTeams[0] : undefined

  const {
    data: teamEnrichedData,
    isLoading: teamDataLoading,
    error: teamDataError
  } = useTeamEnrichedData({
    enabled: selectedTeams.length > 0,
    selectedTeam: selectedTeamForEnrichment
  })

  // Wait for comprehensive data to be available before showing summary cards
  const { isReady: comprehensiveDataReady, isLoading: waitingForComprehensive, status: schedulerStatus } = useWaitForComprehensiveData()

  // Fetch comprehensive team-enriched data for Active by Team calculation
  // This includes epic-level team assignments needed for accurate counting
  const {
    data: comprehensiveTeamData,
    isLoading: comprehensiveTeamDataLoading,
    error: comprehensiveTeamDataError,
    refetch: refetchComprehensiveData
  } = useTeamEnrichedData({
    enabled: comprehensiveDataReady, // Only fetch when comprehensive data is ready
    selectedTeam: undefined // Get all initiatives with team enrichment
  })

  // Log when comprehensive data becomes ready






  // Sorting function
  const handleSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'asc'
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc'
    }
    setSortConfig({ key, direction })
  }



  // Using centralized isOverdue function from dateUtils

  // Helper function to calculate timeline health (for in-progress initiatives only)
  const calculateTimelineHealth = () => {
    // Filter to only in-progress initiatives
    const inProgressInitiatives = processedData.filter((item: any) => {
      const status = typeof item.status === 'string' ? item.status : (item.status?.name || '')
      return status.toLowerCase().includes('progress')
    })

    if (inProgressInitiatives.length === 0) return { percentage: 0, withTimeline: 0, total: 0, missingTimeline: 0 }

    const withCompleteTimeline = inProgressInitiatives.filter((item: any) => {
      // Check if item has complete timeline data
      return item.hasCompleteTimeline ||
        (item.timeline?.hasCompleteTimeline) ||
        (item.startDate && item.endDate)
    })

    const missingTimeline = inProgressInitiatives.filter((item: any) => {
      // Check if item is missing timeline data
      return !(item.hasCompleteTimeline ||
        (item.timeline?.hasCompleteTimeline) ||
        (item.startDate && item.endDate))
    })

    const percentage = Math.round((withCompleteTimeline.length / inProgressInitiatives.length) * 100)

    return {
      percentage,
      withTimeline: withCompleteTimeline.length,
      total: inProgressInitiatives.length,
      missingTimeline: missingTimeline.length,
      missingTimelineItems: missingTimeline
    }
  }

  // Helper function to calculate upcoming deadlines
  const calculateUpcomingDeadlines = () => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)

    const thirtyDaysFromNow = new Date(today)
    thirtyDaysFromNow.setDate(today.getDate() + 30)

    const sixtyDaysFromNow = new Date(today)
    sixtyDaysFromNow.setDate(today.getDate() + 60)

    const upcomingInitiatives = processedData.filter((item: any) => {
      if (!item.endDate) return false

      try {
        const endDateObj = parseISO(item.endDate)
        if (!isValid(endDateObj)) return false

        // Only include non-completed initiatives
        const status = typeof item.status === 'string' ? item.status : (item.status?.name || '')
        const isCompleted = status.toLowerCase().includes('done') ||
          status.toLowerCase().includes('complete') ||
          status.toLowerCase().includes('closed') ||
          status.toLowerCase().includes('awaiting verification') ||
          status.toLowerCase().includes('verification')

        return !isCompleted && endDateObj >= today && endDateObj <= sixtyDaysFromNow
      } catch (error) {
        return false
      }
    })

    const next30Days = upcomingInitiatives.filter((item: any) => {
      const endDateObj = parseISO(item.endDate)
      return endDateObj <= thirtyDaysFromNow
    }).length

    const next31To60Days = upcomingInitiatives.length - next30Days

    return {
      total: upcomingInitiatives.length,
      next30Days,
      next31To60Days
    }
  }

  // Process and filter the data
  const processedData = React.useMemo(() => {

    // Use team-enriched data when available (it has story points and enriched fields)
    // Priority: comprehensiveTeamData > teamEnrichedData > initiativesResponse
    let sourceData

    // Priority 1: Use comprehensive team data when available (has story points for ALL initiatives)
    if (comprehensiveTeamData && comprehensiveTeamData.length > 0) {
      const hasTimelineData = timelineResponse?.data && Object.keys(timelineResponse.data).length > 0
      sourceData = processInitiatives(comprehensiveTeamData, hasTimelineData)
    }
    // Priority 2: Use team-filtered data when teams are selected
    else if (teamEnrichedData && teamEnrichedData.length > 0) {
      let filteredTeamData

      if (selectedTeams.length === 1) {
        // Single team selected: use data directly (already filtered by backend)
        filteredTeamData = teamEnrichedData
      } else {
        // Multiple teams selected: filter by all selected teams
        filteredTeamData = teamEnrichedData.filter(item =>
          item.teams && item.teams.some(team => selectedTeams.includes(team))
        )
      }

      const hasTimelineData = timelineResponse?.data && Object.keys(timelineResponse.data).length > 0
      sourceData = processInitiatives(filteredTeamData, hasTimelineData)
    }
    // Priority 3: Fallback to regular Jira data (no story points)
    else if (initiativesResponse?.data) {
      const hasTimelineData = timelineResponse?.data && Object.keys(timelineResponse.data).length > 0
      sourceData = processInitiatives(initiativesResponse.data, hasTimelineData)
    } else {
      return []
    }

    // Merge timeline data if available
    if (timelineResponse?.data && Object.keys(timelineResponse.data).length > 0) {
      sourceData = timelineService.mergeTimelineWithInitiatives(sourceData, timelineResponse.data)

      // Process timeline data to add dates and debug info
      sourceData = processTimelineData(sourceData)
    }

    let filtered = sourceData    // ============================================
    // FILTER CONFLICT DETECTION
    // ============================================
    // Detect logical conflicts between filters before applying them
    const detectFilterConflicts = () => {
      const conflicts: { message: string; suggestions: string[] }[] = []

      // Conflict 1: Dated Initiatives Only + Paused Status
      if (showTimelineDataOnly && selectedStatuses.includes('Paused')) {
        conflicts.push({
          message: "The 'Dated Initiatives Only' filter excludes paused initiatives, but you've selected 'Paused' in the status filter.",
          suggestions: [
            "Disable the 'Dated Initiatives Only' toggle to see paused initiatives",
            "Remove 'Paused' from the status filter to see only active initiatives with timeline data"
          ]
        })
      }

      // Conflict 2: Overdue Initiatives + Dated Initiatives Only
      // REMOVED: This is NOT a real conflict - overdue initiatives can have dates
      // The combination is valid and should work together

      // Conflict 3: Missing Data + Dated Initiatives Only (mutually exclusive)
      if (showMissingDataOnly && showTimelineDataOnly) {
        conflicts.push({
          message: "'Missing Data' and 'Dated Initiatives Only' filters are mutually exclusive - initiatives can't both have and lack timeline data.",
          suggestions: [
            "Disable one of these filters to see results"
          ]
        })
      }

      // Conflict 4: Timeline Health + Dated Initiatives Only (mutually exclusive)
      if (showTimelineHealthOnly && showTimelineDataOnly) {
        conflicts.push({
          message: "'Timeline Health Issues' shows initiatives missing timeline data, but 'Dated Initiatives Only' requires complete timeline data. These filters are mutually exclusive.",
          suggestions: [
            "Disable one of these filters to see results"
          ]
        })
      }

      return conflicts
    }

    const conflicts = detectFilterConflicts()

    // If there are conflicts, set the conflict state and return empty results
    if (conflicts.length > 0) {
      setFilterConflict({
        hasConflict: true,
        message: conflicts[0].message,
        suggestions: conflicts[0].suggestions
      })
      console.warn('⚠️ Filter conflict detected:', conflicts)
      return [] // Return empty array to prevent errors
    } else {
      // Clear any previous conflicts
      setFilterConflict(null)
    }



    // Apply status filter
    if (selectedStatuses.length > 0) {
      const beforeFilter = filtered.length
      filtered = filtered.filter((item: any) => {
        // Handle both processed data (string) and raw data (object)
        let status = ''
        if (typeof item.status === 'string') {
          status = item.status
        } else if (item.status && typeof item.status === 'object') {
          status = item.status.name || ''
        }

        const matches = selectedStatuses.includes(status)

        return matches
      })
    }

    // Apply PMO assignee filter
    if (selectedPMOAssignees.length > 0) {
      const beforeFilter = filtered.length
      filtered = filtered.filter((item: any) => {
        const pmoAssignee = item.customFields?.pmoAssignee || 'Unassigned'
        const matches = selectedPMOAssignees.includes(pmoAssignee)
        return matches
      })
    }

    // Apply theme filter
    if (selectedThemes.length > 0) {
      const beforeFilter = filtered.length
      filtered = filtered.filter((item: any) => {
        const theme = item.customFields?.theme
        return theme && selectedThemes.includes(theme)
      })
    }

    // Apply PM Lead filter
    if (selectedPMLeads.length > 0) {
      const beforeFilter = filtered.length
      filtered = filtered.filter((item: any) => {
        const pmLead = item.customFields?.pmLead || 'Unassigned'
        const matches = selectedPMLeads.includes(pmLead)
        return matches
      })
    }

    // Apply Labels filter
    if (selectedLabels.length > 0) {
      filtered = filtered.filter((item: any) => {
        const labels = item.labels || []
        // Check if any of the selected labels are present in the item's labels
        return selectedLabels.some(selectedLabel =>
          labels.includes(selectedLabel)
        )
      })
    }

    // Apply end date range filter
    if (endDateRangeStart || endDateRangeEnd) {
      filtered = filtered.filter((item: any) => {
        const endDate = item.endDate
        if (!endDate) return false

        // Parse the end date - handle various formats
        let itemEndDate: Date | null = null
        if (typeof endDate === 'string') {
          // Try parsing ISO format first
          itemEndDate = parseISO(endDate)
          if (!isValid(itemEndDate)) {
            // Try parsing as regular date
            itemEndDate = new Date(endDate)
          }
        } else if (endDate instanceof Date) {
          itemEndDate = endDate
        }

        if (!itemEndDate || !isValid(itemEndDate)) return false

        // Convert filter dates to Date objects for comparison
        // Set time to start/end of day for proper inclusive comparison
        const startFilter = endDateRangeStart ? new Date(endDateRangeStart + 'T00:00:00') : null
        const endFilter = endDateRangeEnd ? new Date(endDateRangeEnd + 'T23:59:59') : null

        // Normalize item end date to start of day for comparison
        const itemEndDateNormalized = new Date(itemEndDate.getFullYear(), itemEndDate.getMonth(), itemEndDate.getDate())

        // Check if item end date falls within the range (inclusive)
        if (startFilter && itemEndDateNormalized < startFilter) return false
        if (endFilter && itemEndDateNormalized > endFilter) return false

        return true
      })
    }

    // Apply missing data filter
    if (showMissingDataOnly) {
      filtered = filtered.filter((item: any) => {
        return !item.sprint || item.sprint.trim() === '' || item.sprint === 'No Team/Sprint'
      })
    }

    // Apply overdue initiatives filter
    if (showOverdueOnly) {
      filtered = filtered.filter((item: any) => {
        // Check multiple date sources (top-level and customFields)
        const endDate = item.endDate || item.customFields?.endDate || null

        // Check if item is overdue (past end date and not complete)
        const status = typeof item.status === 'string' ? item.status : item.status?.name || ''
        const isItemOverdue = isOverdue(endDate, status)

        return isItemOverdue
      })
    }

    // Apply potential risks filter
    if (showPotentialRisksOnly) {
      filtered = filtered.filter((item: any) => {
        const isAtRisk = isInitiativeAtRisk(item)
        return isAtRisk
      })
    }

    // Apply timeline health filter (show in-progress items missing timeline data)
    if (showTimelineHealthOnly) {
      filtered = filtered.filter((item: any) => {
        // Check if item is in progress
        const status = typeof item.status === 'string' ? item.status : (item.status?.name || '')
        const isInProgress = status.toLowerCase().includes('progress')

        // Check if item is missing complete timeline data
        const missingTimeline = !(item.hasCompleteTimeline ||
          (item.timeline?.hasCompleteTimeline) ||
          (item.startDate && item.endDate))

        return isInProgress && missingTimeline
      })
    }

    // Apply upcoming deadlines filter (show items ending within 60 days)
    if (showUpcomingDeadlinesOnly) {
      filtered = filtered.filter((item: any) => {
        if (!item.endDate) return false

        try {
          const endDateObj = parseISO(item.endDate)
          if (!isValid(endDateObj)) return false

          const today = new Date()
          today.setHours(0, 0, 0, 0)

          const sixtyDaysFromNow = new Date(today)
          sixtyDaysFromNow.setDate(today.getDate() + 60)

          // Only include non-completed initiatives
          const status = typeof item.status === 'string' ? item.status : (item.status?.name || '')
          const isCompleted = status.toLowerCase().includes('done') ||
            status.toLowerCase().includes('complete') ||
            status.toLowerCase().includes('closed') ||
            status.toLowerCase().includes('awaiting verification') ||
            status.toLowerCase().includes('verification')

          return !isCompleted && endDateObj >= today && endDateObj <= sixtyDaysFromNow
        } catch (error) {
          return false
        }
      })
    }

    // Apply timeline data filter (show only initiatives with complete timeline data)
    if (showTimelineDataOnly) {
      filtered = filtered.filter((item: any) => {
        // Check if item has complete timeline data
        const hasCompleteTimeline = item.hasCompleteTimeline ||
          (item.timeline?.hasCompleteTimeline) ||
          (item.startDate && item.endDate)

        // Exclude paused initiatives as their timelines are no longer relevant
        const status = typeof item.status === 'string' ? item.status : (item.status?.name || '')
        const isPaused = status.toLowerCase().includes('paused')

        return hasCompleteTimeline && !isPaused
      })
    }

    // Apply global search filter
    if (searchText) {
      const search = searchText.toLowerCase()
      filtered = filtered.filter((item: any) => {
        const status = typeof item.status === 'string' ? item.status : (item.status?.name || '')
        const pmoAssignee = (item as any).customFields?.pmoAssignee || ''
        return (item.key || '').toLowerCase().includes(search) ||
          (item.summary || '').toLowerCase().includes(search) ||
          (item.theme || '').toLowerCase().includes(search) ||
          (item.sprint || '').toLowerCase().includes(search) ||
          pmoAssignee.toLowerCase().includes(search) ||
          status.toLowerCase().includes(search)
      })
    }



    // Apply sorting
    if (sortConfig.key) {
      filtered.sort((a: ProcessedInitiative, b: ProcessedInitiative) => {
        let aValue: any, bValue: any

        switch (sortConfig.key) {
          case 'key':
            aValue = a.key
            bValue = b.key
            break
          case 'summary':
            aValue = a.summary
            bValue = b.summary
            break
          case 'sprint':
            aValue = a.sprint || ''
            bValue = b.sprint || ''
            break
          case 'status':
            aValue = a.status || ''
            bValue = b.status || ''
            break
          case 'progress':
            // Handle undefined/null progress by treating as 0
            aValue = a.progressPercentage ?? 0
            bValue = b.progressPercentage ?? 0
            break
          case 'created':
            aValue = new Date(a.created)
            bValue = new Date(b.created)
            break
          case 'updated':
            aValue = new Date(a.updated)
            bValue = new Date(b.updated)
            break
          case 'startDate':
            // Handle null/missing dates by treating them as earliest date (epoch)
            // This ensures initiatives without dates sort to the bottom when ascending
            aValue = a.startDate ? new Date(a.startDate) : new Date(0)
            bValue = b.startDate ? new Date(b.startDate) : new Date(0)
            break
          case 'endDate':
            // Handle null/missing dates by treating them as earliest date (epoch)
            // This ensures initiatives without dates sort to the bottom when ascending
            aValue = a.endDate ? new Date(a.endDate) : new Date(0)
            bValue = b.endDate ? new Date(b.endDate) : new Date(0)
            break
          default:
            return 0
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1
        }
        return 0
      })
    }





    return filtered
  }, [initiativesResponse, teamEnrichedData, timelineResponse, searchText, selectedStatuses, selectedTeams, selectedPMOAssignees, selectedThemes, selectedPMLeads, selectedLabels, endDateRangeStart, endDateRangeEnd, sortConfig, showMissingDataOnly, showOverdueOnly, showPotentialRisksOnly, showTimelineHealthOnly, showUpcomingDeadlinesOnly, showTimelineDataOnly])

  // Auto-scroll to Today marker when Timeline view is activated
  useEffect(() => {
    if (isTimelineView && processedData.length > 0 && !isLoading && !timelineLoading) {
      // Use a delay to ensure the timeline is fully rendered and DOM is ready
      const timer = setTimeout(() => {
        if (timelineTableRef.current) {
          timelineTableRef.current.scrollToToday()
        }
      }, 300)

      return () => clearTimeout(timer)
    }
  }, [isTimelineView, processedData, isLoading, timelineLoading])

  // Memoized overdue calculation to prevent flickering
  const overdueCalculation = React.useMemo(() => {
    // Only calculate when we have stable data (not loading and has timeline data)
    const hasStableData = !isLoading && processedData.length > 0

    if (!hasStableData) {
      return { overdueCount: 0, isLoading: true }
    }

    // Use the processedData which already contains all the filtered data
    const dataToUse = processedData

    const overdueItems = dataToUse.filter((item: any) =>
      isOverdue(item.endDate, typeof item.status === 'string' ? item.status : item.status?.name || '')
    )

    const overdueCount = overdueItems.length

    return {
      overdueCount,
      isLoading: false,
      overdueItems
    }
  }, [processedData, isLoading])

  /**
   * Get teams from active child epics only (filtering out closed/done epics)
   * Same logic as TeamWorkloadDetailView for consistency
   */
  const getActiveChildEpicTeams = React.useCallback((enrichedInitiative: any): string[] => {
    const childEpicsWithStatus = enrichedInitiative.childEpicsWithStatus || [];

    // If we have the new status data structure, use it for filtering
    if (childEpicsWithStatus.length > 0) {
      const activeChildEpics = filterActiveChildEpics(childEpicsWithStatus);
      const activeTeams = [...new Set(activeChildEpics.map(epic => epic.team).filter(team => team !== 'Unknown'))];

      return activeTeams;
    }

    // Fallback to original behavior if new data structure isn't available
    const childEpicTeams = enrichedInitiative.childEpicTeams || [];
    if (childEpicTeams.length > 0) {
      console.warn(`⚠️ Dashboard: Child epic status filtering not fully implemented for ${enrichedInitiative.key}. Using all child epic teams.`);
    }

    return childEpicTeams;
  }, []);

  // Calculate Active by Team data from RAW unfiltered data (static calculation)
  // This provides a consistent baseline regardless of current Dashboard filters
  const activeByTeamData = React.useMemo(() => {
    // Use raw unfiltered data for static calculation
    const rawInitiatives = initiativesResponse?.data

    // Wait for comprehensive data to be ready before calculating
    // This prevents showing incorrect values during initial load
    const hasStableData = !isLoading &&
                          !comprehensiveTeamDataLoading &&
                          comprehensiveDataReady &&
                          comprehensiveTeamData &&
                          comprehensiveTeamData.length > 0 &&
                          rawInitiatives &&
                          rawInitiatives.length > 0

    if (!hasStableData) {
      return {
        teamCounts: [],
        summary: {
          totalWorkItems: 0,
          uniqueTeams: 0,
          avgWorkItemsPerTeam: 0,
          inProgressInitiatives: 0
        },
        isLoading: true // Add loading flag
      }
    }

    // Debug status distribution first using RAW data
    const statusDistribution = rawInitiatives.reduce((acc, initiative) => {
      const status = typeof initiative.status === 'string' ? initiative.status : (initiative.status?.name || 'Unknown')
      acc[status] = (acc[status] || 0) + 1
      return acc
    }, {})


    // Use comprehensive team data if available, otherwise fall back to regular data
    const dataToUse = comprehensiveTeamData || rawInitiatives

    if (!rawInitiatives || rawInitiatives.length === 0) {
      return {
        teamCounts: [],
        summary: {
          totalWorkItems: 0,
          uniqueTeams: 0,
          avgWorkItemsPerTeam: 0,
          inProgressInitiatives: 0
        }
      }
    }

    // Count work items per team from the filtered data
    // Focus on "Active" work items (In Progress status) for "Active by Team" metric
    const teamWorkCounts = new Map()
    let totalWorkItems = 0
    let inProgressCount = 0
    let debugInfo = []

    // Use comprehensive team data if available for better team assignment detection
    const initiativesToProcess = comprehensiveTeamData || rawInitiatives

    // Create a map of comprehensive data by key for lookup
    const comprehensiveDataMap = new Map()
    if (comprehensiveTeamData) {
      comprehensiveTeamData.forEach(item => {
        comprehensiveDataMap.set(item.key, item)
      })
    }

    rawInitiatives.forEach(initiative => {
      // Get comprehensive team data for this initiative if available
      const enrichedInitiative = comprehensiveDataMap.get(initiative.key) || initiative
      const status = typeof initiative.status === 'string' ? initiative.status : (initiative.status?.name || '')
      const isInProgress = status.toLowerCase().includes('progress')

      if (isInProgress) {
        inProgressCount++
      }

      // ONLY count "In Progress" initiatives for "Active by Team" metric
      // Do NOT count "Awaiting Verification", "Backlog", "Prioritized", etc.
      if (isInProgress) {
        let initiativeAssignments = 0
        const initiativeTeams = new Set() // Track teams for this initiative to avoid double-counting same team

        // Use enriched teams data with child epic status filtering
        let teams = enrichedInitiative.teams || []

        // If this initiative has child epics, apply status filtering
        if (enrichedInitiative.childEpicTeams && enrichedInitiative.childEpicTeams.length > 0) {
          const activeChildEpicTeams = getActiveChildEpicTeams(enrichedInitiative);

          // If we have active child epic teams, use them; otherwise fall back to initiative-level team
          if (activeChildEpicTeams.length > 0) {
            teams = activeChildEpicTeams;
          } else if (enrichedInitiative.team && enrichedInitiative.team !== 'Unknown') {
            // All child epics are inactive, fall back to initiative-level assignment
            teams = [enrichedInitiative.team];
          } else {
            // No active child epics and no initiative-level team
            teams = [];
          }
        }

        if (teams.length > 0) {
          // Assign initiative to ALL teams that have active child epics (Option B with status filtering)
          teams.forEach(team => {
            if (team && team !== 'Unknown' && !initiativeTeams.has(team)) {
              teamWorkCounts.set(team, (teamWorkCounts.get(team) || 0) + 1)
              totalWorkItems++ // Count each team assignment
              initiativeAssignments++
              initiativeTeams.add(team)
              debugInfo.push({
                initiative: initiative.key,
                team,
                source: enrichedInitiative.teamSource || 'teams_array',
                status,
                dataSource: comprehensiveDataMap.has(initiative.key) ? 'comprehensive' : 'regular',
                allTeams: teams, // Keep track of all teams for debugging
                filteredByStatus: enrichedInitiative.childEpicsWithStatus ? true : false
              })
            }
          })
        } else {
          // Fallback: Check if initiative has direct team assignment from sprint
          const sprint = initiative.sprint || initiative.customFields?.sprintField || ''
          if (sprint && sprint !== 'No Team/Sprint') {
            const teamMatch = sprint.match(/^([A-Za-z_]+)/)
            if (teamMatch) {
              const team = teamMatch[1]
              if (team && team !== 'Unknown' && !initiativeTeams.has(team)) {
                teamWorkCounts.set(team, (teamWorkCounts.get(team) || 0) + 1)
                totalWorkItems++ // Count each team assignment
                initiativeAssignments++
                initiativeTeams.add(team)
                debugInfo.push({
                  initiative: initiative.key,
                  team,
                  source: 'initiative_sprint_fallback',
                  sprint,
                  status,
                  dataSource: 'regular'
                })
              }
            }
          }
        }

        // Log initiatives with no team assignments for debugging
        if (initiativeAssignments === 0) {
          debugInfo.push({
            initiative: initiative.key,
            team: 'NO_TEAM_FOUND',
            source: 'unassigned',
            sprint: initiative.sprint,
            teams: enrichedInitiative.teams,
            childEpicTeams: enrichedInitiative.childEpicTeams,
            teamSource: enrichedInitiative.teamSource,
            status,
            dataSource: comprehensiveDataMap.has(initiative.key) ? 'comprehensive' : 'raw'
          })
        }
      }
    })

    // Convert to array format
    const teamCounts = Array.from(teamWorkCounts.entries())
      .map(([team, count]) => ({ team, workItemCount: count }))
      .sort((a, b) => a.team.localeCompare(b.team))

    const uniqueTeams = teamCounts.length
    const avgWorkItemsPerTeam = uniqueTeams > 0 ? totalWorkItems / uniqueTeams : 0

    // Enhanced debugging output
    const assignmentsBySource = debugInfo.reduce((acc, item) => {
      acc[item.source] = (acc[item.source] || 0) + 1
      return acc
    }, {})

    const unassignedInitiatives = debugInfo.filter(item => item.source === 'unassigned')
    const assignedInitiatives = totalWorkItems - unassignedInitiatives.length




    return {
      teamCounts,
      summary: {
        totalWorkItems,
        uniqueTeams,
        avgWorkItemsPerTeam,
        inProgressInitiatives: inProgressCount
      },
      isLoading: false // Data is ready
    }
  }, [initiativesResponse, comprehensiveTeamData, isLoading, comprehensiveTeamDataLoading, comprehensiveDataReady, getActiveChildEpicTeams])

  const handleRefresh = () => {
    refetch()
  }

  // Common compact chip style
  const compactChipStyle = {
    height: 24,
    fontSize: '0.75rem',
    '& .MuiChip-label': {
      color: 'text.primary',
      fontWeight: 500,
      px: 1
    },
    '& .MuiChip-deleteIcon': { fontSize: 16 }
  }



  // Show configuration message if Jira is not configured
  if (!configLoading && jiraConfig && !jiraConfig.isConfigured) {
    return (
      <div>
        <StandardAppBar
          darkMode={darkMode}
          toggleDarkMode={toggleDarkMode}
        />
        <Container maxWidth="xl" sx={{ mt: 2, mb: 3, pb: 8 }}> {/* Added bottom padding for fixed footer */}
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Jira Configuration Required
            </Typography>
            <Typography variant="body1" paragraph>
              The dashboard requires a valid Jira configuration to display initiatives.
              Please contact your administrator to configure the Jira connection settings.
            </Typography>
          </Alert>
        </Container>
      </div>
    )
  }

  if (isError) {
    const searchComponent = (
      <TextField
        placeholder="Search across all columns... (Ctrl+K)"
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        size="small"
        disabled
        sx={{
          flex: 1,
          maxWidth: 380,
          mr: 1.5,
          '& .MuiInputBase-root': {
            backgroundColor: 'rgba(255, 255, 255, 0.15)',
            color: 'white',
            height: 36,
          },
          '& .MuiInputBase-input': {
            color: 'white',
            fontSize: '0.875rem',
            '&::placeholder': {
              color: 'rgba(255, 255, 255, 0.7)',
              opacity: 1
            }
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255, 255, 255, 0.3)',
          }
        }}
      />
    )

    return (
      <div>
        <StandardAppBar
          darkMode={darkMode}
          toggleDarkMode={toggleDarkMode}
          showSearch={true}
          searchComponent={searchComponent}
        />
        <Container maxWidth="xl" sx={{ mt: 2, mb: 3, pb: 8 }}> {/* Added bottom padding for fixed footer */}
          <Alert severity="error" sx={{ mb: 3 }}>
            Failed to load initiatives: {error instanceof Error ? error.message : 'Unknown error'}
            <Button onClick={handleRefresh} sx={{ ml: 2 }}>
              Retry
            </Button>
          </Alert>
        </Container>
      </div>
    )
  }

  const searchComponent = (
    <Tooltip title="Shortcuts: Ctrl+K (focus search), Escape (clear filters)" placement="bottom">
      <TextField
        placeholder="Search across all columns... (Ctrl+K)"
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        size="small"
        sx={{
          flex: 1,
          maxWidth: 380,
          mr: 1.5,
          '& .MuiInputBase-root': {
            backgroundColor: 'rgba(255, 255, 255, 0.15)',
            color: 'white',
            height: 36,
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.25)',
            },
            '&.Mui-focused': {
              backgroundColor: 'rgba(255, 255, 255, 0.25)',
            }
          },
          '& .MuiInputBase-input': {
            color: 'white',
            fontSize: '0.875rem',
            py: 0.75,
            '&::placeholder': {
              color: 'rgba(255, 255, 255, 0.7)',
              opacity: 1
            }
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255, 255, 255, 0.3)',
          },
          '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255, 255, 255, 0.5)',
          },
          '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'white',
          }
        }}
      />
    </Tooltip>
  )

  return (
    <div>
      <StandardAppBar
        darkMode={darkMode}
        toggleDarkMode={toggleDarkMode}
        onRefresh={handleRefresh}
        isLoading={isLoading}
        showSearch={true}
        searchComponent={searchComponent}
      />

      <Container maxWidth="xl" sx={{ mt: 2, mb: 3, pb: 8 }}> {/* Added bottom padding for fixed footer */}


        {/* Loading State */}
        {(isLoading || (selectedTeams.length > 0 && teamDataLoading)) && (
          <Box display="flex" justifyContent="center" my={4}>
            <CircularProgress />
            <Typography variant="body2" sx={{ ml: 2, alignSelf: 'center' }}>
              Loading initiatives...
            </Typography>
          </Box>
        )}

        {/* Executive Summary Cards - Always show when not loading */}
        {!isLoading && (
          <CollapsibleSection
            title="Executive Summary"
            icon={<Assignment color="primary" sx={{ fontSize: 18 }} />}
            defaultExpanded={true}
            sx={{ mb: 1.5 }} // Reduced from 2 to 1.5
            contentSx={{ px: 0, py: 0 }} // Added py: 0 to remove vertical padding
          >
            <Grid container spacing={1}> {/* Reduced from 1.5 to 1 */}
              {/* 1. Status Breakdown - Comprehensive Pipeline Overview */}
              <Grid item xs={6} sm={4} md={2}>
                <Card sx={{
                  height: '100%',
                  border: '1px solid',
                  borderColor: (() => {
                    const backlogCount = processedData.filter((item: any) => {
                      const status = typeof item.status === 'string' ? item.status : (item.status?.name || '')
                      return status.toLowerCase().includes('backlog')
                    }).length
                    const pausedCount = processedData.filter((item: any) => {
                      const status = typeof item.status === 'string' ? item.status : (item.status?.name || '')
                      return isPaused(status)
                    }).length
                    const totalStalled = backlogCount + pausedCount
                    const stalledPercentage = processedData.length > 0 ? (totalStalled / processedData.length) * 100 : 0
                    return stalledPercentage > 40 ? 'rgba(211, 47, 47, 0.3)' : stalledPercentage > 20 ? 'rgba(237, 108, 2, 0.3)' : 'rgba(46, 125, 50, 0.3)'
                  })()
                }}>
                  <CardContent sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 60, // Reduced from 75 to 60
                    py: 1, // Reduced from 1.5 to 1
                    px: 1.5 // Reduced from 2 to 1.5
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}> {/* Reduced from 1 to 0.5 */}
                      <DashboardIcon color="primary" sx={{ mr: 0.75, fontSize: 16 }} /> {/* Reduced icon size and margin */}
                      <Typography variant="subtitle2" sx={{ fontSize: '0.8rem', fontWeight: 600 }}>Status Breakdown</Typography> {/* Smaller font */}
                    </Box>
                    <Typography variant="h5" color="primary.main" sx={{ mb: 0.25, fontSize: '1.25rem', fontWeight: 'bold' }}> {/* Reduced size and margin */}
                      {processedData.length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem', lineHeight: 1.1 }}> {/* Smaller font and tighter line height */}
                      {(() => {
                        const backlogCount = processedData.filter((item: any) => {
                          const status = typeof item.status === 'string' ? item.status : (item.status?.name || '')
                          return status.toLowerCase().includes('backlog')
                        }).length
                        const inProgressCount = processedData.filter((item: any) => {
                          const status = typeof item.status === 'string' ? item.status : (item.status?.name || '')
                          return status.toLowerCase().includes('progress')
                        }).length
                        const awaitingCount = processedData.filter((item: any) => {
                          const status = typeof item.status === 'string' ? item.status : (item.status?.name || '')
                          return status.toLowerCase().includes('awaiting') || status.toLowerCase().includes('verification')
                        }).length
                        const pausedCount = processedData.filter((item: any) => {
                          const status = typeof item.status === 'string' ? item.status : (item.status?.name || '')
                          return isPaused(status)
                        }).length

                        if (processedData.length === 0) return 'No initiatives'

                        // Always show all four status categories
                        return `${backlogCount} Backlog, ${inProgressCount} In Progress, ${awaitingCount} Awaiting Verification, ${pausedCount} Paused`
                      })()}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* 2. Active by Team - Average Workload Distribution */}
              <Grid item xs={6} sm={4} md={2}>
                <Card sx={{
                  height: '100%',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease-in-out',
                  border: (() => {
                    if (isLoading || comprehensiveTeamDataLoading || activeByTeamData.isLoading) return '1px solid'
                    // Use filtered data for workload calculation
                    const avgWorkload = activeByTeamData.summary.avgWorkItemsPerTeam
                    return avgWorkload > 6 ? '2px solid' : avgWorkload > 4 ? '2px solid' : '1px solid'
                  })(),
                  borderColor: (() => {
                    if (isLoading || comprehensiveTeamDataLoading || activeByTeamData.isLoading) return 'divider'
                    // Use filtered data for workload calculation
                    const avgWorkload = activeByTeamData.summary.avgWorkItemsPerTeam
                    return avgWorkload > 6 ? 'error.main' : avgWorkload > 4 ? 'warning.main' : 'success.main'
                  })(),
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 3,
                    borderColor: 'primary.main'
                  }
                }}
                  onClick={() => navigate('/team-workload')}>
                  <CardContent sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 60, // Reduced from 75 to 60
                    py: 1, // Reduced from 1.5 to 1
                    px: 1.5 // Reduced from 2 to 1.5
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}> {/* Reduced from 1 to 0.5 */}
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Group color="info" sx={{ mr: 0.75, fontSize: 16 }} /> {/* Reduced icon size and margin */}
                        <Typography variant="subtitle2" sx={{ fontSize: '0.8rem', fontWeight: 600 }}>Active by Team</Typography> {/* Smaller font */}
                      </Box>
                      <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                        Click for details
                      </Typography>
                    </Box>
                    {(isLoading || comprehensiveTeamDataLoading || activeByTeamData.isLoading) ? (
                      <>
                        <Typography variant="h5" sx={{
                          mb: 0.25, // Reduced from 0.5 to 0.25
                          fontSize: '1.25rem', // Reduced from 1.5rem to 1.25rem
                          fontWeight: 'bold',
                          color: 'text.secondary'
                        }}>
                          --
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem', lineHeight: 1.1 }}> {/* Smaller font and tighter line height */}
                          {activeByTeamData.isLoading ? 'Waiting for comprehensive data...' : 'Loading team data...'}
                        </Typography>
                      </>
                    ) : (
                      <>
                        <Typography variant="h5" sx={{
                          mb: 0.25, // Reduced from 0.5 to 0.25
                          fontSize: '1.25rem', // Reduced from 1.5rem to 1.25rem
                          fontWeight: 'bold',
                          color: (() => {
                            // Use filtered data for workload calculation
                            const avgWorkload = activeByTeamData.summary.avgWorkItemsPerTeam
                            return avgWorkload > 6 ? 'error.main' : avgWorkload > 4 ? 'warning.main' : 'success.main'
                          })()
                        }}>
                          {activeByTeamData.summary.avgWorkItemsPerTeam.toFixed(1)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem', lineHeight: 1.1 }}> {/* Smaller font and tighter line height */}
                          {(() => {
                            const { totalWorkItems, uniqueTeams } = activeByTeamData.summary
                            if (totalWorkItems === 0) return 'No active work items'
                            return `${totalWorkItems} work items across ${uniqueTeams} teams (avg per team)`
                          })()}
                        </Typography>
                      </>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* 3. Timeline Health - Data Quality Indicator */}
              <Grid item xs={6} sm={4} md={2}>
                <Card sx={{
                  height: '100%',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-1px)',
                    boxShadow: 2,
                    backgroundColor: showTimelineHealthOnly ? 'rgba(33, 150, 243, 0.12)' : 'action.hover'
                  },
                  backgroundColor: showTimelineHealthOnly ? 'rgba(33, 150, 243, 0.08)' : 'inherit',
                  border: '1px solid',
                  borderColor: (() => {
                    if (showTimelineHealthOnly) {
                      return 'rgba(33, 150, 243, 0.5)'
                    }
                    const timelineHealth = calculateTimelineHealth()
                    return timelineHealth.percentage >= 80 ? 'rgba(46, 125, 50, 0.3)' :
                      timelineHealth.percentage >= 60 ? 'rgba(237, 108, 2, 0.3)' :
                        'rgba(211, 47, 47, 0.3)'
                  })()
                }}
                  role="button"
                  onClick={() => {
                    setShowTimelineHealthOnly(!showTimelineHealthOnly)
                    // Clear other card filters (maintain card mutual exclusivity)
                    // Preserve user-selected filters for combination
                    if (!showTimelineHealthOnly) {
                      setShowOverdueOnly(false)
                      setShowPotentialRisksOnly(false)
                      setShowUpcomingDeadlinesOnly(false)
                      setShowMissingDataOnly(false)
                      setShowTimelineDataOnly(false)
                    }
                  }}>
                  <CardContent sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 60, // Reduced from 75 to 60
                    py: 1, // Reduced from 1.5 to 1
                    px: 1.5 // Reduced from 2 to 1.5
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}> {/* Reduced from 1 to 0.5 */}
                      <Schedule color="info" sx={{ mr: 0.75, fontSize: 16 }} /> {/* Reduced icon size and margin */}
                      <Typography variant="subtitle2" sx={{ fontSize: '0.8rem', fontWeight: 600 }}>Timeline Health</Typography> {/* Smaller font */}
                    </Box>
                    <Typography variant="h5" sx={{
                      mb: 0.25, // Reduced from 0.5 to 0.25
                      fontSize: '1.25rem', // Reduced from 1.5rem to 1.25rem
                      fontWeight: 'bold',
                      color: (() => {
                        const timelineHealth = calculateTimelineHealth()
                        const percentage = timelineHealth.percentage
                        if (percentage <= 25) return '#d32f2f' // Red
                        if (percentage <= 75) return '#f57c00' // Orange
                        return '#1976d2' // Blue
                      })()
                    }}>
                      {(() => {
                        const timelineHealth = calculateTimelineHealth()
                        return `${timelineHealth.percentage}%`
                      })()}
                    </Typography>
                    <Typography variant="body2" color={showTimelineHealthOnly ? "info.main" : "text.secondary"} sx={{ fontSize: '0.7rem', lineHeight: 1.1, fontWeight: showTimelineHealthOnly ? 'bold' : 'normal' }}> {/* Smaller font and tighter line height */}
                      {(() => {
                        if (showTimelineHealthOnly) {
                          return "🔍 Missing timeline data - Click to clear"
                        }
                        const timelineHealth = calculateTimelineHealth()
                        if (timelineHealth.total === 0) return 'No in-progress initiatives'
                        return `${timelineHealth.withTimeline}/${timelineHealth.total} in-progress with timeline`
                      })()}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* 4. Upcoming Deadlines - Proactive Management */}
              <Grid item xs={6} sm={4} md={2}>
                <Card sx={{
                  height: '100%',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-1px)',
                    boxShadow: 2,
                    backgroundColor: showUpcomingDeadlinesOnly ? 'rgba(255, 152, 0, 0.12)' : 'action.hover'
                  },
                  backgroundColor: showUpcomingDeadlinesOnly ? 'rgba(255, 152, 0, 0.08)' : 'inherit',
                  border: '1px solid',
                  borderColor: (() => {
                    if (showUpcomingDeadlinesOnly) {
                      return 'rgba(255, 152, 0, 0.5)'
                    }
                    const deadlines = calculateUpcomingDeadlines()
                    return deadlines.total > 10 ? 'rgba(211, 47, 47, 0.3)' :
                      deadlines.total > 5 ? 'rgba(237, 108, 2, 0.3)' :
                        'rgba(46, 125, 50, 0.3)'
                  })()
                }}
                  role="button"
                  onClick={() => {
                    setShowUpcomingDeadlinesOnly(!showUpcomingDeadlinesOnly)
                    // Clear other card filters (maintain card mutual exclusivity)
                    // Preserve user-selected filters for combination
                    if (!showUpcomingDeadlinesOnly) {
                      setShowOverdueOnly(false)
                      setShowPotentialRisksOnly(false)
                      setShowTimelineHealthOnly(false)
                      setShowMissingDataOnly(false)
                      setShowTimelineDataOnly(false)
                    }
                  }}>
                  <CardContent sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 60, // Reduced from 75 to 60
                    py: 1, // Reduced from 1.5 to 1
                    px: 1.5 // Reduced from 2 to 1.5
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}> {/* Reduced from 1 to 0.5 */}
                      <EventAvailable color="warning" sx={{ mr: 0.75, fontSize: 16 }} /> {/* Reduced icon size and margin */}
                      <Typography variant="subtitle2" sx={{ fontSize: '0.8rem', fontWeight: 600 }}>Upcoming Deadlines</Typography> {/* Smaller font */}
                    </Box>
                    <Typography variant="h5" color="warning.main" sx={{ mb: 0.25, fontSize: '1.25rem', fontWeight: 'bold' }}> {/* Reduced size and margin */}
                      {(() => {
                        const deadlines = calculateUpcomingDeadlines()
                        return deadlines.total
                      })()}
                    </Typography>
                    <Typography variant="body2" color={showUpcomingDeadlinesOnly ? "warning.main" : "text.secondary"} sx={{ fontSize: '0.7rem', lineHeight: 1.1, fontWeight: showUpcomingDeadlinesOnly ? 'bold' : 'normal' }}> {/* Smaller font and tighter line height */}
                      {(() => {
                        if (showUpcomingDeadlinesOnly) {
                          return "🔍 Upcoming deadlines filtered - Click to clear"
                        }
                        const deadlines = calculateUpcomingDeadlines()
                        if (deadlines.total === 0) return 'No upcoming deadlines'
                        const parts = []
                        if (deadlines.next30Days > 0) parts.push(`${deadlines.next30Days} this month`)
                        if (deadlines.next31To60Days > 0) parts.push(`${deadlines.next31To60Days} next month`)
                        return parts.length > 0 ? parts.join(', ') : 'All deadlines tracked'
                      })()}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* 5. Potential Risks - Initiatives at Risk of Delay */}
              <Grid item xs={6} sm={4} md={2}>
                <Card sx={{
                  height: '100%',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-1px)',
                    boxShadow: 2,
                    backgroundColor: showPotentialRisksOnly ? 'rgba(255, 152, 0, 0.12)' : 'action.hover'
                  },
                  backgroundColor: showPotentialRisksOnly ? 'rgba(255, 152, 0, 0.08)' : 'inherit',
                  border: '1px solid',
                  borderColor: (() => {
                    if (isLoading || comprehensiveTeamDataLoading || activeByTeamData.isLoading) {
                      return 'divider' // Gray for loading
                    }
                    if (showPotentialRisksOnly) {
                      return 'rgba(255, 152, 0, 0.5)'
                    }
                    const atRiskCount = processedData.filter((item: any) => isInitiativeAtRisk(item)).length
                    if (atRiskCount > 10) {
                      return 'rgba(244, 67, 54, 0.5)' // Red for high risk
                    } else if (atRiskCount > 5) {
                      return 'rgba(255, 152, 0, 0.5)' // Orange for medium risk
                    } else if (atRiskCount > 0) {
                      return 'rgba(255, 193, 7, 0.5)' // Yellow for low risk
                    }
                    return 'rgba(158, 158, 158, 0.3)' // Gray for no risk
                  })()
                }}
                  onClick={() => {
                    // Toggle potential risks filter
                    setShowPotentialRisksOnly(!showPotentialRisksOnly)

                    // Clear other card filters (maintain card mutual exclusivity)
                    // Preserve user-selected filters for combination
                    if (!showPotentialRisksOnly) {
                      setShowOverdueOnly(false)
                      setShowTimelineHealthOnly(false)
                      setShowUpcomingDeadlinesOnly(false)
                      setShowMissingDataOnly(false)
                      setShowTimelineDataOnly(false)
                    }
                  }}
                >
                  <CardContent sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 60,
                    py: 1,
                    px: 1.5
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                      <Warning color="warning" sx={{ mr: 0.75, fontSize: 16 }} />
                      <Typography variant="subtitle2" sx={{ fontSize: '0.8rem', fontWeight: 600 }}>Potential Risks</Typography>
                    </Box>
                    {(isLoading || comprehensiveTeamDataLoading || activeByTeamData.isLoading) ? (
                      <>
                        <Typography variant="h5" sx={{
                          mb: 0.25,
                          fontSize: '1.25rem',
                          fontWeight: 'bold',
                          color: 'text.secondary'
                        }}>
                          --
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem', lineHeight: 1.1 }}>
                          {activeByTeamData.isLoading ? 'Waiting for comprehensive data...' : 'Loading risk data...'}
                        </Typography>
                      </>
                    ) : (
                      <>
                        <Typography variant="h5" color="warning.main" sx={{ mb: 0.25, fontSize: '1.25rem', fontWeight: 'bold' }}>
                          {(() => {
                            const atRiskInitiatives = processedData.filter((item: any) => isInitiativeAtRisk(item))
                            return atRiskInitiatives.length
                          })()}
                        </Typography>
                        <Typography variant="body2" color={showPotentialRisksOnly ? "warning.main" : "text.secondary"} sx={{ fontSize: '0.75rem', fontWeight: showPotentialRisksOnly ? 'bold' : 'normal' }}>
                          {showPotentialRisksOnly ? "🔍 At-risk filtered - Click to clear" : "Initiatives at risk of delay"}
                        </Typography>
                      </>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* 6. Overdue Initiatives - Behind Schedule */}
              <Grid item xs={6} sm={4} md={2}>
                <Card sx={{
                  height: '100%',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-1px)',
                    boxShadow: 2,
                    backgroundColor: showOverdueOnly ? 'rgba(244, 67, 54, 0.12)' : 'action.hover'
                  },
                  backgroundColor: showOverdueOnly ? 'rgba(244, 67, 54, 0.08)' : 'inherit',
                  border: (() => {
                    if (showOverdueOnly) {
                      return '2px solid'
                    }
                    return (overdueCalculation.overdueCount > 0) ? '2px solid' : '1px solid'
                  })(),
                  borderColor: (() => {
                    if (showOverdueOnly) {
                      return 'rgba(244, 67, 54, 0.6)'
                    }
                    return (overdueCalculation.overdueCount > 0) ? 'rgba(244, 67, 54, 0.4)' : 'divider'
                  })()
                }}
                  onClick={() => {
                    setShowOverdueOnly(!showOverdueOnly)
                    // Clear other card filters (maintain card mutual exclusivity)
                    // Preserve user-selected filters for combination
                    if (!showOverdueOnly) {
                      setShowPotentialRisksOnly(false)
                      setShowTimelineHealthOnly(false)
                      setShowUpcomingDeadlinesOnly(false)
                      setShowMissingDataOnly(false)
                      setShowTimelineDataOnly(false)
                    }
                  }}
                >
                  <CardContent sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 60, // Reduced from 75 to 60
                    py: 1, // Reduced from 1.5 to 1
                    px: 1.5 // Reduced from 2 to 1.5
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}> {/* Reduced from 1 to 0.5 */}
                      <Warning sx={{
                        mr: 0.75, // Reduced from 1 to 0.75
                        fontSize: 16, // Reduced from 18 to 16
                        color: showOverdueOnly ? '#d32f2f' : 'error.main'
                      }} />
                      <Typography variant="subtitle2" sx={{
                        fontSize: '0.8rem', // Reduced from 0.85rem to 0.8rem
                        fontWeight: 600,
                        color: showOverdueOnly ? '#d32f2f' : 'text.primary'
                      }}>Overdue Initiatives</Typography>
                    </Box>
                    <Typography variant="h5" color="error.main" sx={{ mb: 0.25, fontSize: '1.25rem', fontWeight: 'bold' }}> {/* Reduced size and margin */}
                      {(() => {
                        const count = overdueCalculation.isLoading ? '...' : overdueCalculation.overdueCount
                        return count
                      })()}
                    </Typography>
                    <Typography variant="body2" color={showOverdueOnly ? "#d32f2f" : "text.secondary"} sx={{ fontSize: '0.7rem', lineHeight: 1.1, fontWeight: showOverdueOnly ? 'bold' : 'normal' }}> {/* Smaller font and tighter line height */}
                      {(() => {
                        if (showOverdueOnly) {
                          return "🔍 Overdue filtered - Click to clear"
                        }

                        if (overdueCalculation.isLoading) {
                          return 'Loading timeline data...'
                        }

                        const { overdueCount } = overdueCalculation
                        if (overdueCount === 0) return 'All initiatives on schedule'
                        return `${overdueCount} past end date`
                      })()}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </CollapsibleSection>
        )}

        {/* Compact Filters - Always show when not loading */}
        {!isLoading && (
          <CollapsibleSection
            title="Filters & Views"
            icon={<FilterList color="primary" sx={{ fontSize: 18 }} />}
            defaultExpanded={true}
            sx={{ mb: 1.5 }} // Reduced from 2 to 1.5
            headerActions={
              <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', flexShrink: 0 }}>
                <SavedViews
                  currentFilters={{
                    statuses: selectedStatuses,
                    teams: selectedTeams,
                    pmoAssignees: selectedPMOAssignees,
                    themes: selectedThemes,
                    pmLeads: selectedPMLeads,
                    labels: selectedLabels,
                    endDateRangeStart: endDateRangeStart,
                    endDateRangeEnd: endDateRangeEnd
                  }}
                  currentSort={sortConfig}
                  currentSearch={searchText}
                  onLoadView={(view) => {
                    // Load the saved view
                    if (view.filters.statuses) {
                      setSelectedStatuses(view.filters.statuses)
                    }
                    if (view.filters.teams) {
                      setSelectedTeams(view.filters.teams)
                    }
                    if (view.filters.pmoAssignees) {
                      setSelectedPMOAssignees(view.filters.pmoAssignees)
                    }
                    if (view.filters.themes) {
                      setSelectedThemes(view.filters.themes)
                    }
                    if (view.filters.pmLeads) {
                      setSelectedPMLeads(view.filters.pmLeads)
                    }
                    if (view.filters.labels) {
                      setSelectedLabels(view.filters.labels)
                    }
                    if (view.filters.endDateRangeStart) {
                      setEndDateRangeStart(view.filters.endDateRangeStart)
                    }
                    if (view.filters.endDateRangeEnd) {
                      setEndDateRangeEnd(view.filters.endDateRangeEnd)
                    }
                    if (view.sortConfig) {
                      setSortConfig(view.sortConfig)
                    }
                    if (view.searchText) {
                      setSearchText(view.searchText)
                    }
                  }}
                />
                <Button
                  variant="text"
                  size="small"
                  startIcon={<Clear sx={{ fontSize: 14 }} />}
                  onClick={() => {
                    setSearchText('')
                    setSelectedStatuses([])
                    setSelectedTeams([])
                    setSelectedPMOAssignees([])
                    setSelectedThemes([])
                    setSelectedPMLeads([])
                    setSelectedLabels([])
                    setEndDateRangeStart('')
                    setEndDateRangeEnd('')
                    setSortConfig({ key: null, direction: 'asc' })
                    // Clear card filters
                    setShowOverdueOnly(false)
                    setShowPotentialRisksOnly(false)
                    setShowTimelineHealthOnly(false)
                    setShowUpcomingDeadlinesOnly(false)
                    setShowMissingDataOnly(false)
                    setShowTimelineDataOnly(false)
                  }}
                  disabled={!searchText && selectedStatuses.length === 0 && selectedTeams.length === 0 && selectedPMOAssignees.length === 0 && selectedThemes.length === 0 && selectedPMLeads.length === 0 && selectedLabels.length === 0 && !endDateRangeStart && !endDateRangeEnd && !sortConfig.key && !showOverdueOnly && !showPotentialRisksOnly && !showTimelineHealthOnly && !showUpcomingDeadlinesOnly && !showMissingDataOnly && !showTimelineDataOnly}
                  sx={{
                    minWidth: 'auto',
                    textTransform: 'none',
                    fontSize: '0.75rem',
                    py: 0.5,
                    px: 1,
                    height: 32,
                    color: (searchText || selectedStatuses.length > 0 || selectedTeams.length > 0 || selectedPMOAssignees.length > 0 || selectedThemes.length > 0 || selectedPMLeads.length > 0 || selectedLabels.length > 0 || endDateRangeStart || endDateRangeEnd || sortConfig.key || showOverdueOnly || showPotentialRisksOnly || showTimelineHealthOnly || showUpcomingDeadlinesOnly || showMissingDataOnly || showTimelineDataOnly) ? 'error.main' : 'text.disabled',
                    '&:hover': {
                      backgroundColor: 'error.50',
                      color: 'error.dark'
                    },
                    '&:disabled': {
                      color: 'text.disabled'
                    }
                  }}
                >
                  Clear
                </Button>
              </Box>
            }
            contentSx={{ px: 1.5, pb: 1.5 }} // Reduced padding from 2 to 1.5
          >


            {/* All Filters Row - Including End Date */}
            <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap', alignItems: 'center' }}> {/* Changed from baseline to center for better filter alignment */}
              <StatusFilter
                selectedStatuses={selectedStatuses}
                onStatusesChange={handleStatusChange}
              />
              <TeamFilter
                selectedTeams={selectedTeams}
                onTeamsChange={setSelectedTeams}
              />
              <PMOAssigneeFilter
                selectedAssignees={selectedPMOAssignees}
                onAssigneeChange={setSelectedPMOAssignees}
              />
              <ThemeFilter
                selectedThemes={selectedThemes}
                onThemesChange={setSelectedThemes}
              />
              <PMLeadFilter
                selectedPMLeads={selectedPMLeads}
                onPMLeadsChange={setSelectedPMLeads}
              />
              <LabelsFilter
                selectedLabels={selectedLabels}
                onLabelsChange={setSelectedLabels}
              />
              {/* End Date Filter - Now in first row */}
              <EndDateRangeFilter
                startDate={endDateRangeStart}
                endDate={endDateRangeEnd}
                onStartDateChange={setEndDateRangeStart}
                onEndDateChange={setEndDateRangeEnd}
                onClear={() => {
                  setEndDateRangeStart('')
                  setEndDateRangeEnd('')
                }}
              />
            </Box>



            {/* Compact Active Filters Display */}
            {(searchText || selectedStatuses.length > 0 || selectedTeams.length > 0 || selectedPMOAssignees.length > 0 || selectedThemes.length > 0 || selectedPMLeads.length > 0 || selectedLabels.length > 0 || endDateRangeStart || endDateRangeEnd || sortConfig.key || showOverdueOnly || showPotentialRisksOnly || showTimelineHealthOnly || showUpcomingDeadlinesOnly || showMissingDataOnly || showTimelineDataOnly) && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mb: 1.5 }}>
                {searchText && (
                  <Chip
                    label={`Global: ${searchText}`}
                    size="small"
                    onDelete={() => setSearchText('')}
                    color="primary"
                    variant="outlined"
                    sx={compactChipStyle}
                  />
                )}
                {selectedStatuses.map((status) => (
                  <Chip
                    key={status}
                    label={`Status: ${status}`}
                    size="small"
                    onDelete={() => setSelectedStatuses(prev => prev.filter(s => s !== status))}
                    color="primary"
                    variant="outlined"
                    sx={compactChipStyle}
                  />
                ))}

                {selectedTeams.map((team) => (
                  <Chip
                    key={team}
                    label={`Team: ${team}`}
                    size="small"
                    onDelete={() => setSelectedTeams(prev => prev.filter(t => t !== team))}
                    color="info"
                    variant="outlined"
                    sx={compactChipStyle}
                  />
                ))}
                {selectedPMOAssignees.map((assignee) => (
                  <Chip
                    key={assignee}
                    label={`PMO: ${assignee}`}
                    size="small"
                    onDelete={() => setSelectedPMOAssignees(prev => prev.filter(a => a !== assignee))}
                    color="secondary"
                    variant="outlined"
                    sx={compactChipStyle}
                  />
                ))}
                {selectedThemes.map((theme) => (
                  <Chip
                    key={theme}
                    label={`Theme: ${theme}`}
                    size="small"
                    onDelete={() => setSelectedThemes(prev => prev.filter(t => t !== theme))}
                    color="success"
                    variant="outlined"
                    sx={compactChipStyle}
                  />
                ))}
                {selectedPMLeads.map((pmLead) => (
                  <Chip
                    key={pmLead}
                    label={`PM Lead: ${pmLead}`}
                    size="small"
                    onDelete={() => setSelectedPMLeads(prev => prev.filter(l => l !== pmLead))}
                    color="warning"
                    variant="outlined"
                    sx={compactChipStyle}
                  />
                ))}
                {selectedLabels.map((label) => (
                  <Chip
                    key={label}
                    label={`Label: ${label}`}
                    size="small"
                    onDelete={() => setSelectedLabels(prev => prev.filter(l => l !== label))}
                    color="primary"
                    variant="outlined"
                    sx={compactChipStyle}
                  />
                ))}
                {(endDateRangeStart || endDateRangeEnd) && (
                  <Chip
                    label={(() => {
                      if (endDateRangeStart && endDateRangeEnd) {
                        return `End Date: ${endDateRangeStart} to ${endDateRangeEnd}`
                      } else if (endDateRangeStart) {
                        return `End Date: from ${endDateRangeStart}`
                      } else {
                        return `End Date: until ${endDateRangeEnd}`
                      }
                    })()}
                    size="small"
                    onDelete={() => {
                      setEndDateRangeStart('')
                      setEndDateRangeEnd('')
                    }}
                    color="secondary"
                    variant="outlined"
                    sx={compactChipStyle}
                  />
                )}
                {sortConfig.key && (
                  <Chip
                    label={`Sort: ${sortConfig.key} ${sortConfig.direction === 'asc' ? '↑' : '↓'}`}
                    size="small"
                    onDelete={() => setSortConfig({ key: null, direction: 'asc' })}
                    color="info"
                    variant="outlined"
                  />
                )}

                {/* Card Filter Chips */}
                {showOverdueOnly && (
                  <Chip
                    label="🔍 Overdue Initiatives"
                    size="small"
                    onDelete={() => setShowOverdueOnly(false)}
                    color="error"
                    variant="filled"
                    sx={compactChipStyle}
                  />
                )}
                {showPotentialRisksOnly && (
                  <Chip
                    label="🔍 Potential Risks"
                    size="small"
                    onDelete={() => setShowPotentialRisksOnly(false)}
                    color="warning"
                    variant="filled"
                    sx={compactChipStyle}
                  />
                )}
                {showTimelineHealthOnly && (
                  <Chip
                    label="🔍 Timeline Health Issues"
                    size="small"
                    onDelete={() => setShowTimelineHealthOnly(false)}
                    color="info"
                    variant="filled"
                    sx={compactChipStyle}
                  />
                )}
                {showUpcomingDeadlinesOnly && (
                  <Chip
                    label="🔍 Upcoming Deadlines"
                    size="small"
                    onDelete={() => setShowUpcomingDeadlinesOnly(false)}
                    color="warning"
                    variant="filled"
                    sx={compactChipStyle}
                  />
                )}
                {showMissingDataOnly && (
                  <Chip
                    label="🔍 Missing Data"
                    size="small"
                    onDelete={() => setShowMissingDataOnly(false)}
                    color="default"
                    variant="filled"
                    sx={compactChipStyle}
                  />
                )}
                {showTimelineDataOnly && (
                  <Chip
                    label="📅 Dated Initiatives Only"
                    size="small"
                    onDelete={() => setShowTimelineDataOnly(false)}
                    color="success"
                    variant="filled"
                    sx={compactChipStyle}
                  />
                )}

                <Typography variant="body2" color="text.secondary">
                  {processedData.length} results
                </Typography>
              </Box>
            )}
          </CollapsibleSection>
        )}

        {/* Initiatives Table - Always visible when not loading */}
        {!isLoading && (
          <Paper sx={{ width: '100%', overflow: 'hidden' }}>
            <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                {/* Left side: Title and count in single line */}
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2 }}>
                  <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
                    Initiatives Overview
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {processedData.length} of {initiativesResponse?.data?.length || 0} initiatives displayed
                    {(searchText || selectedStatuses.length > 0 || selectedTeams.length > 0 || selectedPMOAssignees.length > 0 || selectedThemes.length > 0 || selectedPMLeads.length > 0 || selectedLabels.length > 0 || sortConfig.key || showOverdueOnly || showPotentialRisksOnly || showTimelineHealthOnly || showUpcomingDeadlinesOnly || showMissingDataOnly || showTimelineDataOnly) && (
                      <Chip
                        label="Filtered"
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ ml: 1, height: 20 }}
                      />
                    )}
                  </Typography>
                </Box>

                {/* Right side: Timeline Data Toggle and Timeline View Toggle */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <TimelineDataToggle
                    showTimelineDataOnly={showTimelineDataOnly}
                    onToggle={setShowTimelineDataOnly}
                    disabled={isLoading}
                  />
                  <Tooltip title={isTimelineView ? "Switch to Table View (Ctrl+Shift+T)" : "Switch to Timeline View (Ctrl+Shift+T)"}>
                    <Button
                      variant={isTimelineView ? "contained" : "outlined"}
                      size="small"
                      onClick={() => setIsTimelineView(!isTimelineView)}
                      startIcon={isTimelineView ? <ViewList /> : <ViewTimeline />}
                      sx={{
                        minWidth: 110,
                        px: 2,
                        py: 0.5,
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 'bold',
                        fontSize: '0.875rem',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          transform: 'translateY(-1px)',
                          boxShadow: 2
                        }
                      }}
                    >
                      {isTimelineView ? 'Table View' : 'Timeline View'}
                    </Button>
                  </Tooltip>
                </Box>
              </Box>
            </Box>

            {/* Filter Conflict Warning */}
            {filterConflict && filterConflict.hasConflict && (
              <Box sx={{ px: 2, pt: 2 }}>
                <Alert
                  severity="warning"
                  sx={{
                    mb: 0,
                    '& .MuiAlert-message': {
                      width: '100%'
                    }
                  }}
                  onClose={() => setFilterConflict(null)}
                >
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Filter Conflict Detected
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1.5 }}>
                    {filterConflict.message}
                  </Typography>
                  {filterConflict.suggestions.length > 0 && (
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 0.5 }}>
                        Suggestions:
                      </Typography>
                      <Box component="ul" sx={{ mt: 0, mb: 0, pl: 2.5 }}>
                        {filterConflict.suggestions.map((suggestion, index) => (
                          <Typography
                            key={index}
                            component="li"
                            variant="body2"
                            sx={{ mb: 0.5 }}
                          >
                            {suggestion}
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  )}
                </Alert>
              </Box>
            )}

            {/* Conditional rendering: Timeline View or Table View */}
            <Box
              sx={{
                transition: 'all 0.3s ease-in-out',
                opacity: isLoading ? 0.7 : 1
              }}
            >
              {isTimelineView ? (
                <TimelineTable
                  ref={timelineTableRef}
                  data={processedData}
                  isLoading={isLoading || timelineLoading}
                />
              ) : (
                <TableContainer sx={{
                  maxHeight: 800,
                  // Add pulse animation for warning icons
                  '@keyframes pulse': {
                    '0%': {
                      opacity: 1,
                    },
                    '50%': {
                      opacity: 0.5,
                    },
                    '100%': {
                      opacity: 1,
                    },
                  },
                }}>
                  <Table stickyHeader sx={{
                    '& .MuiTableCell-root': {
                      py: 0.75, // Reduced padding for more compact rows
                      px: 1.5
                    }
                  }}>
                    <TableHead>
                      <TableRow>
                        <SortableHeader
                          label="Epic Rank"
                          sortKey="epicRank"
                          currentSort={sortConfig}
                          onSort={handleSort}
                          minWidth={140}
                          align="center"
                        />
                        <SortableHeader
                          label="Key"
                          sortKey="key"
                          currentSort={sortConfig}
                          onSort={handleSort}
                          minWidth={150}
                        />
                        <SortableHeader
                          label="Summary"
                          sortKey="summary"
                          currentSort={sortConfig}
                          onSort={handleSort}
                          minWidth={300}
                        />
                        <SortableHeader
                          label="Team/Sprint"
                          sortKey="sprint"
                          currentSort={sortConfig}
                          onSort={handleSort}
                          minWidth={120}
                        />
                        <SortableHeader
                          label="Status"
                          sortKey="status"
                          currentSort={sortConfig}
                          onSort={handleSort}
                          minWidth={150}
                        />
                        <SortableHeader
                          label="Progress"
                          sortKey="progress"
                          currentSort={sortConfig}
                          onSort={handleSort}
                          minWidth={100}
                          align="center"
                        />
                        <SortableHeader
                          label="Start Date"
                          sortKey="startDate"
                          currentSort={sortConfig}
                          onSort={handleSort}
                          minWidth={140}
                          align="center"
                        />
                        <SortableHeader
                          label="End Date"
                          sortKey="endDate"
                          currentSort={sortConfig}
                          onSort={handleSort}
                          minWidth={140}
                          align="center"
                        />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {processedData.length > 0 ? (
                        processedData.map((initiative: ProcessedInitiative) => (
                          <TableRow
                            key={initiative.id}
                            hover
                            onClick={() => navigate(`/initiative/${initiative.key}`)}
                            sx={{
                              '&:last-child td, &:last-child th': { border: 0 },
                              cursor: 'pointer',
                              '&:hover': {
                                backgroundColor: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)'
                              }
                            }}
                          >
                            {/* Epic Rank */}
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 500, textAlign: 'center' }}>
                                {initiative.epicRank === '999' ? '-' : initiative.epicRank}
                              </Typography>
                            </TableCell>

                            {/* Key */}
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography
                                  variant="body2"
                                  fontWeight="medium"
                                  color="primary"
                                  sx={{
                                    cursor: 'pointer',
                                    '&:hover': { textDecoration: 'underline' }
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation() // Prevent row click
                                    navigate(`/initiative/${initiative.key}`)
                                  }}
                                >
                                  {initiative.key}
                                </Typography>
                                <Tooltip title="Open in JIRA">
                                  <Link
                                    href={`https://redislabs.atlassian.net/browse/${initiative.key}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{ display: 'flex', alignItems: 'center' }}
                                    onClick={(e) => e.stopPropagation()} // Prevent row click
                                  >
                                    <OpenInNew sx={{ fontSize: 14 }} />
                                  </Link>
                                </Tooltip>
                              </Box>
                            </TableCell>

                            {/* Summary */}
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {initiative.summary}
                              </Typography>
                            </TableCell>

                            {/* Team/Sprint */}
                            <TableCell>
                              {initiative.sprint ? (
                                <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                                  {initiative.sprint}
                                </Typography>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  No Team/Sprint
                                </Typography>
                              )}
                            </TableCell>

                            {/* Status */}
                            <TableCell>
                              <Chip
                                label={typeof initiative.status === 'string'
                                  ? initiative.status
                                  : initiative.status?.name || 'Unknown Status'}
                                size="small"
                                sx={{
                                  backgroundColor: getStatusHexColor(
                                    typeof initiative.status === 'string'
                                      ? initiative.status
                                      : initiative.status?.name || 'Unknown Status',
                                    darkMode
                                  ),
                                  color: 'white',
                                  fontWeight: 500,
                                  '&:hover': {
                                    backgroundColor: getStatusHexColor(
                                      typeof initiative.status === 'string'
                                        ? initiative.status
                                        : initiative.status?.name || 'Unknown Status',
                                      darkMode
                                    )
                                  }
                                }}
                              />
                            </TableCell>

                            {/* Progress */}
                            <TableCell align="center">
                              <ProgressDisplay
                                percentage={initiative.progressPercentage || 0}
                                variant="text"
                                size="medium"
                                precision={1}
                                showDashForEmpty={!initiative.totalStoryPoints || initiative.totalStoryPoints === 0}
                                tooltip={initiative.totalStoryPoints && initiative.totalStoryPoints > 0
                                  ? `${initiative.completedStoryPoints || 0}/${initiative.totalStoryPoints} story points completed`
                                  : 'No story points data available'}
                              />
                            </TableCell>

                            {/* Start Date */}
                            <TableCell align="center">
                              {initiative.startDate ? (
                                <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                                  {new Date(initiative.startDate).toLocaleDateString('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric'
                                  })}
                                </Typography>
                              ) : (
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                                  No Start Date
                                </Typography>
                              )}
                            </TableCell>

                            {/* End Date */}
                            <TableCell align="center">
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                {/* End Date Text */}
                                {initiative.endDate ? (
                                  <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                                    {new Date(initiative.endDate).toLocaleDateString('en-US', {
                                      month: 'short',
                                      day: 'numeric',
                                      year: 'numeric'
                                    })}
                                  </Typography>
                                ) : (
                                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                                    No End Date
                                  </Typography>
                                )}

                                {/* Status Indicators (Warning and Pause) */}
                                <StatusIndicators
                                  endDate={initiative.endDate}
                                  status={initiative.status}
                                  context="table"
                                />
                              </Box>
                            </TableCell>

                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={8} sx={{ textAlign: 'center', py: 8 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                              <SearchOff sx={{ fontSize: 48, color: 'text.secondary' }} />
                              <Typography variant="h6" color="text.secondary">
                                No results found
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400, textAlign: 'center' }}>
                                {(searchText || selectedStatuses.length > 0 || selectedTeams.length > 0 || selectedPMOAssignees.length > 0 || selectedThemes.length > 0 || selectedPMLeads.length > 0 || selectedLabels.length > 0 || showOverdueOnly || showPotentialRisksOnly || showTimelineHealthOnly || showUpcomingDeadlinesOnly || showMissingDataOnly || showTimelineDataOnly)
                                  ? 'Try adjusting your filters or search terms to find what you\'re looking for.'
                                  : 'No initiatives are currently available. Try refreshing the page or check back later.'
                                }
                              </Typography>
                              {(searchText || selectedStatuses.length > 0 || selectedTeams.length > 0 || selectedPMOAssignees.length > 0 || selectedThemes.length > 0 || selectedPMLeads.length > 0 || selectedLabels.length > 0 || showOverdueOnly || showPotentialRisksOnly || showTimelineHealthOnly || showUpcomingDeadlinesOnly || showMissingDataOnly || showTimelineDataOnly) && (
                                <Button
                                  variant="outlined"
                                  startIcon={<ClearAll />}
                                  onClick={() => {
                                    setSearchText('')
                                    setSelectedStatuses([])
                                    setSelectedTeams([])
                                    setSelectedPMOAssignees([])
                                    setSelectedThemes([])
                                    setSelectedPMLeads([])
                                    setSelectedLabels([])
                                    setSortConfig({ key: null, direction: 'asc' })
                                    // Clear card filters
                                    setShowOverdueOnly(false)
                                    setShowPotentialRisksOnly(false)
                                    setShowTimelineHealthOnly(false)
                                    setShowUpcomingDeadlinesOnly(false)
                                    setShowMissingDataOnly(false)
                                    setShowTimelineDataOnly(false)
                                  }}
                                  sx={{ mt: 1 }}
                                >
                                  Clear All Filters
                                </Button>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </Paper>
        )}





      </Container>
      <Footer />
    </div>
  )
}

export default Dashboard
